import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useAuth from '../../../Hooks/useAuth';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminFormFieldError,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminConfigLockedField,
  AdminSupportingText,
  AdminHeaderAction,
  adminFormDetailInputClasses,
  adminFormDetailSelectClasses,
  adminFormDetailTextareaClasses,
  adminFormTwoColGridClass,
  AdminFormDropdownChevron,
  AdminCompactFormPage,
  ADMIN_FORM_PANEL_CLASS,
  ADMIN_PAGE_TITLE_CLASS,
} from '../shared/adminUx';
import {
  CASE_ACTION_MODULE_OPTIONS,
  CASE_ACTIONS_ROUTE,
  CONDITION_OPERATOR_OPTIONS,
  CONFIRMATION_REQUIREMENT_OPTIONS,
  EVIDENCE_REQUIREMENT_OPTIONS,
  REASON_REQUIREMENT_OPTIONS,
  APPEAL_CONFIRMATION_FIXED_MESSAGE,
  applyCaseActionDerivedFields,
  applyCaseActionSelectionChange,
  buildCaseActionScopeKey,
  buildSampleCaseActionRules,
  createEmptyCaseActionForm,
  createEmptyCondition,
  formatCaseActionDateTime,
  formatConditionsPreview,
  getActionTargetLabel,
  getActionTypeLabel,
  getActionTypesForModuleRecord,
  getCaseActionModuleLabel,
  getConditionFieldLabel,
  getConditionFieldsForSource,
  getConditionSourceLabel,
  getConditionSourcesForScope,
  getConditionValueLabel,
  getConditionValueOptions,
  getConfirmationRequirementLabel,
  getEvidenceRequirementLabel,
  getEvidenceSourceLabel,
  getEvidenceSourcesForScope,
  getReasonRequirementLabel,
  getRecordTypeLabel,
  getRecordTypeOptions,
  getTargetResolutionsForRule,
  nextCaseActionRuleId,
  normalizeCaseActionForm,
  operatorIsMulti,
  requiresConfirmationMessage,
  requiresEvidenceSource,
} from '../../../data/caseActionsConfig';
import {
  canAddCaseActionRule,
  canEditCaseActionRule,
  canSaveCaseActionRule,
  canViewCaseActions,
  getEditRuleSource,
  getPublishedRule,
  saveCaseActionRule,
} from '../../../data/caseActionRuleWorkflow';
import { matchesUrlName } from '../../../utils/slugify';

function emptyForm() {
  return {
    ...createEmptyCaseActionForm(),
    version: 1,
    pendingDraft: null,
    configurationHistory: [],
    testHistory: [],
  };
}

function mergeIncomingRule(raw, ruleIdParam) {
  const normalized = normalizeCaseActionForm(raw || {});
  return {
    ...emptyForm(),
    ...normalized,
    id: normalized.id ?? ruleIdParam ?? '',
    version: raw?.version ?? 1,
    pendingDraft: raw?.pendingDraft ?? null,
    configurationHistory: raw?.configurationHistory ?? [],
    testHistory: raw?.testHistory ?? [],
  };
}

const TextValue = ({ label, value, mono = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
    {mono ? (
      <p className="text-offblack text-sm py-2 font-mono break-words">{value || '—'}</p>
    ) : (
      <AdminDetailFieldValue className="py-2 break-words whitespace-pre-wrap">{value || '—'}</AdminDetailFieldValue>
    )}
  </div>
);

const MultiChecklist = ({ label, options, values, onChange, disabled, error, required = false }) => (
  <div className="space-y-1 w-full min-w-0">
    <AdminDetailFieldLabel>
      {label} {required ? <span className="text-alertred">*</span> : null}
    </AdminDetailFieldLabel>
    <div className="space-y-2">
      {disabled || !options.length ? (
        <AdminDetailFieldValue className="py-2">—</AdminDetailFieldValue>
      ) : (
        options.map((option) => (
          <label key={option.value} className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 mt-0.5 text-primaryblue shrink-0"
              checked={(values || []).includes(option.value)}
              onChange={(e) => {
                const next = new Set(values || []);
                if (e.target.checked) next.add(option.value);
                else next.delete(option.value);
                onChange([...next]);
              }}
            />
            <span className="text-offblack text-sm break-words">{option.label}</span>
          </label>
        ))
      )}
    </div>
    {error ? <AdminFormFieldError>{error}</AdminFormFieldError> : null}
  </div>
);

const withCurrentOption = (options, value, getLabel) => {
  if (!value || options.some((option) => option.value === value)) return options;
  return [...options, { value, label: `${getLabel(value) || value} (Unavailable)` }];
};

const ActionRuleDetail = () => {
  const navigate = useNavigate();
  const { ruleName } = useParams();
  const location = useLocation();
  const { auth } = useAuth();
  const isCreate = location.pathname.endsWith('/add');
  const isEdit = location.pathname.includes('/edit');
  const isView = !isCreate && !isEdit;

  const canView = canViewCaseActions(auth);
  const canAdd = canAddCaseActionRule(auth);
  const canEdit = canEditCaseActionRule(auth);
  const canSave = canSaveCaseActionRule(auth);

  const ruleFromState = location.state?.rule;
  const existingRulesFromState = location.state?.existingRules;

  const allRules = useMemo(
    () => existingRulesFromState ?? buildSampleCaseActionRules(),
    [existingRulesFromState],
  );

  const publishedBaseline = useMemo(() => {
    if (isCreate) return null;
    if (ruleFromState?.id) {
      const fromList = allRules.find((rule) => rule.id === ruleFromState.id);
      return getPublishedRule(fromList || ruleFromState);
    }
    if (ruleName) {
      const found = allRules.find((rule) => matchesUrlName(rule.ruleName, ruleName));
      return getPublishedRule(found || null);
    }
    return null;
  }, [isCreate, ruleFromState, ruleName, allRules]);

  const resolvedSource = useMemo(() => {
    if (ruleFromState && isView) {
      const foundRecord = allRules.find((rule) => rule.id === ruleFromState.id) || ruleFromState;
      return mergeIncomingRule(getPublishedRule(foundRecord) || foundRecord, foundRecord.id);
    }
    if (ruleFromState) {
      const foundRecord = allRules.find((rule) => rule.id === ruleFromState.id) || ruleFromState;
      const source = isEdit ? getEditRuleSource(foundRecord) : getPublishedRule(foundRecord) || foundRecord;
      return mergeIncomingRule(source, foundRecord.id);
    }
    if (!isCreate && ruleName) {
      const found = allRules.find((rule) => matchesUrlName(rule.ruleName, ruleName));
      if (found) {
        const source = isEdit ? getEditRuleSource(found) : getPublishedRule(found);
        return mergeIncomingRule(source, found.id);
      }
    }
    return emptyForm();
  }, [ruleFromState, isCreate, isEdit, isView, ruleName, allRules]);

  const [formData, setFormData] = useState(resolvedSource);
  const [errors, setErrors] = useState({});

  const scopeLocked = Boolean(!isCreate && formData.id);
  const needsEvidenceSource = requiresEvidenceSource(formData.evidenceRequirement);
  const needsConfirmationMessage = requiresConfirmationMessage(formData.confirmationRequirement);
  const appealConfirmFixed =
    formData.module === 'appeals'
    && formData.actionType === 'save_confirm_decision'
    && needsConfirmationMessage;

  const recordTypeOptions = useMemo(
    () => withCurrentOption(getRecordTypeOptions(formData.module), formData.recordType, (value) => getRecordTypeLabel(formData.module, value)),
    [formData.module, formData.recordType],
  );
  const actionTypeOptions = useMemo(
    () => withCurrentOption(getActionTypesForModuleRecord(formData.module, formData.recordType), formData.actionType, getActionTypeLabel),
    [formData.module, formData.recordType, formData.actionType],
  );
  const actionTargetOptions = useMemo(
    () => withCurrentOption(
      getTargetResolutionsForRule(formData.module, formData.recordType),
      formData.actionTarget,
      (value) => getActionTargetLabel(formData.module, formData.recordType, value),
    ),
    [formData.module, formData.recordType, formData.actionTarget],
  );
  const evidenceSourceOptions = useMemo(
    () => withCurrentOption(getEvidenceSourcesForScope(), formData.evidenceSource, getEvidenceSourceLabel),
    [formData.evidenceSource],
  );
  const conditionSourceOptions = useMemo(
    () => getConditionSourcesForScope(formData.module),
    [formData.module],
  );
  const hasCompatibleConditions = conditionSourceOptions.some(
    (source) => getConditionFieldsForSource(source.value).length > 0,
  );

  useEffect(() => {
    setFormData(resolvedSource);
  }, [resolvedSource]);

  const clearFieldErrors = (field) => {
    setErrors((prev) => {
      const nextErr = {
        ...prev,
        [field]: undefined,
        activation: undefined,
        duplicate: undefined,
        overlap: undefined,
        conflict: undefined,
        concurrent: undefined,
        fixedScope: undefined,
        permission: undefined,
        checksUnavailable: undefined,
        form: undefined,
        conditions: undefined,
      };
      if (field === 'conditions') {
        Object.keys(nextErr).forEach((key) => {
          if (key.startsWith('condition_')) delete nextErr[key];
        });
      }
      return nextErr;
    });
  };

  const handleChange = (field, value) => {
    clearFieldErrors(field);
    setFormData((prev) => applyCaseActionSelectionChange(prev, field, value));
  };

  const updateCondition = (index, patch) => {
    setErrors((prev) => ({ ...prev, [`condition_${index}`]: undefined, conditions: undefined, activation: undefined }));
    setFormData((prev) => {
      const conditions = [...(prev.conditions || [])];
      const current = conditions[index] || createEmptyCondition();
      if (patch.source && patch.source !== current.source) {
        conditions[index] = { ...current, source: patch.source, field: '', operator: '', values: [] };
      } else {
        conditions[index] = { ...current, ...patch };
        if (patch.field) conditions[index].values = [];
        if (patch.operator && !operatorIsMulti(patch.operator)) {
          conditions[index].values = (conditions[index].values || []).slice(0, 1);
        }
      }
      return { ...prev, conditions };
    });
  };

  const buildRecord = (nowIso, userLabel) => {
    const derived = applyCaseActionDerivedFields(normalizeCaseActionForm(formData));
    const id = formData.id || nextCaseActionRuleId(allRules);
    const isNew = !formData.id;
    return {
      ...derived,
      id,
      ruleName: derived.ruleName.trim(),
      changeReason: derived.changeReason.trim(),
      version: formData.version || publishedBaseline?.version || 1,
      pendingDraft: null,
      configurationHistory: formData.configurationHistory || [],
      testHistory: formData.testHistory || [],
      dateAdded: isNew ? nowIso : formData.dateAdded || nowIso,
      addedBy: isNew ? userLabel : formData.addedBy || userLabel,
      lastUpdated: nowIso,
      lastUpdatedBy: userLabel,
    };
  };

  const handleSave = () => {
    const nowIso = new Date().toISOString();
    const userLabel = 'Olivia Brown';
    setErrors((prev) => ({ ...prev, activation: undefined }));

    if ((isCreate && !canAdd) || (isEdit && !canEdit) || !canSave) {
      setErrors({
        permission: 'You do not have permission to save this Rule.',
        activation: 'You do not have permission to save this Rule.',
      });
      return;
    }

    const record = buildRecord(nowIso, userLabel);
    let result;
    try {
      result = saveCaseActionRule(allRules, record, publishedBaseline || {}, {
        originalLastUpdated: resolvedSource.lastUpdated,
        originalScopeKey: publishedBaseline ? buildCaseActionScopeKey(publishedBaseline) : undefined,
        canSave: true,
        checksAvailable: true,
      });
    } catch {
      setErrors({
        checksUnavailable: 'Required checks could not be completed. Try again.',
        activation: 'Required checks could not be completed. Try again.',
      });
      return;
    }

    if (!result.success) {
      const next = { ...result.errors };
      if (result.summary.length) next.activation = result.summary.join(' ');
      setErrors(next);
      return;
    }

    navigate(CASE_ACTIONS_ROUTE, { state: { rules: result.rules } });
  };

  const renderSelect = (name, label, options, placeholder, disabledExtra = false, required = true) => {
    if (isView) {
      const selected = options.find((option) => option.value === formData[name]);
      return <TextValue label={label} value={selected?.label || formData[name] || '—'} />;
    }
    const emptyCompatible = !disabledExtra && !options.length;
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>
          {label} {required ? <span className="text-alertred">*</span> : null}
        </AdminDetailFieldLabel>
        <div className="relative w-full min-w-0">
          <select
            id={`act-${name}-select`}
            value={formData[name] || ''}
            title={options.find((option) => option.value === formData[name])?.label || placeholder}
            onChange={(e) => handleChange(name, e.target.value)}
            disabled={disabledExtra || emptyCompatible}
            className={adminFormDetailSelectClasses(disabledExtra || emptyCompatible, Boolean(errors[name]))}
          >
            <option value="">{emptyCompatible ? 'No compatible choices available' : placeholder}</option>
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <AdminFormDropdownChevron />
        </div>
        {errors[name] ? <AdminFormFieldError>{errors[name]}</AdminFormFieldError> : null}
      </div>
    );
  };

  const renderTextarea = (name, label, placeholder, required = false, readOnly = false) => {
    if (isView) return <TextValue label={label} value={formData[name]} />;
    if (readOnly) {
      return <AdminConfigLockedField label={label} value={formData[name] || '—'} isView={false} />;
    }
    return (
      <div className="space-y-1 w-full min-w-0">
        <AdminDetailFieldLabel>
          {label} {required ? <span className="text-alertred">*</span> : null}
        </AdminDetailFieldLabel>
        <textarea
          id={`act-${name}`}
          placeholder={placeholder}
          value={formData[name] || ''}
          onChange={(e) => handleChange(name, e.target.value)}
          className={adminFormDetailTextareaClasses(false, Boolean(errors[name]))}
          rows={3}
        />
        {errors[name] ? <AdminFormFieldError>{errors[name]}</AdminFormFieldError> : null}
      </div>
    );
  };

  const conditionBlock = (
    <div className="space-y-3">
      {errors.conditions ? <AdminFormFieldError>{errors.conditions}</AdminFormFieldError> : null}
      {(formData.conditions || []).length ? (
        <div className="space-y-3">
          {(formData.conditions || []).map((condition, index) => {
            const fieldOptions = withCurrentOption(
              getConditionFieldsForSource(condition.source),
              condition.field,
              (value) => getConditionFieldLabel(condition.source, value),
            );
            const valueOptions = getConditionValueOptions(condition.source, condition.field);
            const multi = operatorIsMulti(condition.operator);
            return (
              <div key={condition.id || index} className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <AdminDetailFieldLabel className="!mb-0">Condition</AdminDetailFieldLabel>
                  <button
                    type="button"
                    onClick={() => handleChange(
                      'conditions',
                      (formData.conditions || []).filter((_, conditionIndex) => conditionIndex !== index),
                    )}
                    className="shrink-0 inline-flex items-center justify-center leading-none text-offblack hover:text-alertred"
                    aria-label="Remove Condition"
                  >
                    <i className="fi fi-rs-trash text-sm" aria-hidden />
                  </button>
                </div>
                {errors[`condition_${index}`] ? (
                  <AdminFormFieldError>{errors[`condition_${index}`]}</AdminFormFieldError>
                ) : null}
                <div className="space-y-1 w-full min-w-0">
                  <AdminDetailFieldLabel>Condition Source <span className="text-alertred">*</span></AdminDetailFieldLabel>
                  <div className="relative w-full min-w-0">
                    <select
                      value={condition.source || ''}
                      onChange={(e) => updateCondition(index, { source: e.target.value })}
                      className={adminFormDetailSelectClasses(false, Boolean(errors[`condition_${index}`]))}
                    >
                      <option value="">Select source</option>
                      {withCurrentOption(conditionSourceOptions, condition.source, getConditionSourceLabel).map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    <AdminFormDropdownChevron />
                  </div>
                </div>
                <div className="space-y-1 w-full min-w-0">
                  <AdminDetailFieldLabel>Condition Field <span className="text-alertred">*</span></AdminDetailFieldLabel>
                  <div className="relative w-full min-w-0">
                    <select
                      value={condition.field || ''}
                      onChange={(e) => updateCondition(index, { field: e.target.value })}
                      disabled={!condition.source}
                      className={adminFormDetailSelectClasses(!condition.source, Boolean(errors[`condition_${index}`]))}
                    >
                      <option value="">{condition.source ? 'Select field' : 'Select source first'}</option>
                      {fieldOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    <AdminFormDropdownChevron />
                  </div>
                </div>
                <div className="space-y-1 w-full min-w-0">
                  <AdminDetailFieldLabel>Operator <span className="text-alertred">*</span></AdminDetailFieldLabel>
                  <div className="relative w-full min-w-0">
                    <select
                      value={condition.operator || ''}
                      onChange={(e) => updateCondition(index, { operator: e.target.value })}
                      disabled={!condition.field}
                      className={adminFormDetailSelectClasses(!condition.field)}
                    >
                      <option value="">Select operator</option>
                      {CONDITION_OPERATOR_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    <AdminFormDropdownChevron />
                  </div>
                </div>
                {multi ? (
                  <MultiChecklist
                    label="Comparison Value"
                    options={valueOptions}
                    values={condition.values}
                    onChange={(value) => updateCondition(index, { values: value })}
                    disabled={!condition.field || !condition.operator}
                    required
                  />
                ) : (
                  <div className="space-y-1 w-full min-w-0">
                    <AdminDetailFieldLabel>Comparison Value <span className="text-alertred">*</span></AdminDetailFieldLabel>
                    <div className="relative w-full min-w-0">
                      <select
                        value={(condition.values || [])[0] || ''}
                        onChange={(e) => updateCondition(index, { values: e.target.value ? [e.target.value] : [] })}
                        disabled={!condition.field || !condition.operator}
                        className={adminFormDetailSelectClasses(!condition.field || !condition.operator)}
                      >
                        <option value="">Select value</option>
                        {withCurrentOption(
                          valueOptions,
                          (condition.values || [])[0],
                          (value) => getConditionValueLabel(condition.source, condition.field, value),
                        ).map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      <AdminFormDropdownChevron />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <AdminDetailFieldValue className="py-2">
          {hasCompatibleConditions ? 'No Conditions added' : 'No compatible Conditions available'}
        </AdminDetailFieldValue>
      )}
      {hasCompatibleConditions ? (
        <button
          type="button"
          onClick={() => handleChange('conditions', [
            ...(formData.conditions || []),
            createEmptyCondition(),
          ])}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primaryblue hover:text-brightblue"
        >
          <i className="fi fi-rs-plus text-xs" aria-hidden />
          Add Condition
        </button>
      ) : null}
    </div>
  );

  const hasValidationErrors = Boolean(
    errors.activation
    || errors.duplicate
    || errors.overlap
    || errors.conflict
    || errors.concurrent
    || errors.fixedScope
    || errors.permission
    || errors.checksUnavailable
    || errors.form,
  );

  if (!canView || (isCreate && !canAdd) || (isEdit && !canEdit)) {
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Case Actions</AdminPageTitle>
        <AdminSupportingText className="text-mediumgray">
          You do not have permission to {isView ? 'view' : isCreate ? 'add' : 'edit'} this Rule.
        </AdminSupportingText>
      </AdminLayout>
    );
  }

  if (isView) {
    const conditionsPreview = formatConditionsPreview(formData.conditions);
    return (
      <AdminLayout>
        <AdminPageTitle className={ADMIN_PAGE_TITLE_CLASS}>Case Action Rule Detail</AdminPageTitle>
        <div className={`space-y-4 ${ADMIN_PAGE_TITLE_CLASS}`}>
          <DashboardMainContainer className={ADMIN_FORM_PANEL_CLASS}>
            <div className={`${adminFormTwoColGridClass} gap-y-4`}>
              <TextValue label="Rule ID" value={formData.id} mono />
              <TextValue label="Rule Name" value={formData.ruleName} />
              <TextValue label="Module" value={getCaseActionModuleLabel(formData.module)} />
              <TextValue label="Record Type" value={getRecordTypeLabel(formData.module, formData.recordType)} />
              <TextValue label="Action Type" value={getActionTypeLabel(formData.actionType)} />
              <TextValue
                label="Action Target"
                value={getActionTargetLabel(formData.module, formData.recordType, formData.actionTarget)}
              />
              <TextValue label="Reason Requirement" value={getReasonRequirementLabel(formData.reasonRequirement)} />
              <TextValue label="Evidence Requirement" value={getEvidenceRequirementLabel(formData.evidenceRequirement)} />
              <TextValue
                label="Evidence Source"
                value={needsEvidenceSource ? (getEvidenceSourceLabel(formData.evidenceSource) || 'Unavailable') : 'Not applicable'}
              />
              <TextValue
                label="Confirmation Requirement"
                value={getConfirmationRequirementLabel(formData.confirmationRequirement)}
              />
              <div className="sm:col-span-2">
                <TextValue
                  label="Confirmation Message"
                  value={needsConfirmationMessage ? (formData.confirmationMessage || '—') : 'Not applicable'}
                />
              </div>
              <div className="sm:col-span-2">
                <TextValue
                  label="Conditions"
                  value={conditionsPreview.lines.length ? conditionsPreview.lines.join('\n') : 'No Conditions'}
                />
              </div>
              <TextValue label="Date Added" value={formatCaseActionDateTime(formData.dateAdded)} />
              <TextValue label="Added By" value={formData.addedBy} />
              <TextValue label="Last Updated" value={formatCaseActionDateTime(formData.lastUpdated)} />
              <TextValue label="Last Updated By" value={formData.lastUpdatedBy} />
            </div>
          </DashboardMainContainer>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <AdminCompactFormPage
        title={isCreate ? 'Add Case Action Rule' : 'Edit Case Action Rule'}
        onSubmit={handleSave}
        showSave={false}
      >
        {formData.id ? (
          <AdminConfigLockedField label="Rule ID" value={formData.id} mono isView={false} />
        ) : null}
        <div className="space-y-1 w-full min-w-0">
          <AdminDetailFieldLabel>
            Rule Name <span className="text-alertred">*</span>
          </AdminDetailFieldLabel>
          <input
            id="act-rule-name"
            type="text"
            placeholder="Enter rule name"
            value={formData.ruleName || ''}
            onChange={(e) => handleChange('ruleName', e.target.value)}
            className={adminFormDetailInputClasses(false, Boolean(errors.ruleName))}
          />
          {errors.ruleName ? <AdminFormFieldError>{errors.ruleName}</AdminFormFieldError> : null}
        </div>
        {scopeLocked ? (
          <>
            <AdminConfigLockedField label="Module" value={getCaseActionModuleLabel(formData.module)} isView={false} />
            <AdminConfigLockedField
              label="Record Type"
              value={getRecordTypeLabel(formData.module, formData.recordType)}
              isView={false}
            />
            <AdminConfigLockedField label="Action Type" value={getActionTypeLabel(formData.actionType)} isView={false} />
            <AdminConfigLockedField
              label="Action Target"
              value={getActionTargetLabel(formData.module, formData.recordType, formData.actionTarget)}
              isView={false}
            />
          </>
        ) : (
          <>
            {renderSelect('module', 'Module', CASE_ACTION_MODULE_OPTIONS, 'Select module')}
            {renderSelect(
              'recordType',
              'Record Type',
              recordTypeOptions,
              formData.module ? 'Select record type' : 'Select module first',
              !formData.module,
            )}
            {renderSelect(
              'actionType',
              'Action Type',
              actionTypeOptions,
              formData.recordType ? 'Select action type' : 'Select record type first',
              !formData.recordType,
            )}
            {renderSelect(
              'actionTarget',
              'Action Target',
              actionTargetOptions,
              formData.actionType ? 'Select action target' : 'Select action type first',
              !formData.actionType,
            )}
          </>
        )}
        {renderSelect('reasonRequirement', 'Reason Requirement', REASON_REQUIREMENT_OPTIONS, 'Select reason requirement')}
        {renderSelect(
          'evidenceRequirement',
          'Evidence Requirement',
          EVIDENCE_REQUIREMENT_OPTIONS,
          'Select evidence requirement',
        )}
        {needsEvidenceSource ? (
          renderSelect('evidenceSource', 'Evidence Source', evidenceSourceOptions, 'Select evidence source')
        ) : (
          <AdminConfigLockedField label="Evidence Source" value="Not applicable" isView={false} />
        )}
        {renderSelect(
          'confirmationRequirement',
          'Confirmation Requirement',
          CONFIRMATION_REQUIREMENT_OPTIONS,
          'Select confirmation requirement',
        )}
        {needsConfirmationMessage ? (
          appealConfirmFixed ? (
            <AdminConfigLockedField
              label="Confirmation Message"
              value={formData.confirmationMessage || APPEAL_CONFIRMATION_FIXED_MESSAGE}
              isView={false}
            />
          ) : (
            renderTextarea('confirmationMessage', 'Confirmation Message', 'Enter a confirmation message', true)
          )
        ) : (
          <AdminConfigLockedField label="Confirmation Message" value="Not applicable" isView={false} />
        )}
        {conditionBlock}
        {renderTextarea('changeReason', 'Change Reason', 'Enter a change reason', true)}
        {hasValidationErrors ? (
          <div className="space-y-2">
            {errors.permission ? <AdminFormFieldError>{errors.permission}</AdminFormFieldError> : null}
            {errors.checksUnavailable ? <AdminFormFieldError>{errors.checksUnavailable}</AdminFormFieldError> : null}
            {errors.fixedScope ? <AdminFormFieldError>{errors.fixedScope}</AdminFormFieldError> : null}
            {errors.duplicate ? <AdminFormFieldError>{errors.duplicate}</AdminFormFieldError> : null}
            {errors.overlap ? <AdminFormFieldError>{errors.overlap}</AdminFormFieldError> : null}
            {errors.conflict ? <AdminFormFieldError>{errors.conflict}</AdminFormFieldError> : null}
            {errors.concurrent ? <AdminFormFieldError>{errors.concurrent}</AdminFormFieldError> : null}
            {errors.form ? <AdminFormFieldError>{errors.form}</AdminFormFieldError> : null}
            {errors.activation ? <AdminFormFieldError>{errors.activation}</AdminFormFieldError> : null}
          </div>
        ) : null}
        <div className="flex items-center justify-end">
          <AdminHeaderAction type="submit" disabled={!canSave}>Save</AdminHeaderAction>
        </div>
      </AdminCompactFormPage>
    </AdminLayout>
  );
};

export default ActionRuleDetail;
